import { z } from 'zod';
import type { PermissionSet, RoleSummary } from './roles';

/**
 * The auth contract, in `shared` because the browser, the API and (later) the MCP
 * dispatcher all need the same idea of who is asking.
 *
 * Requirement 10: Arif distributes access. The server's `REGISTRATION_MODE` decides how.
 * In `invite` (the default) only the FIRST account can register - that is the bootstrap
 * admin of a fresh instance - and every account after it is created by an admin
 * (`POST /api/auth/users`), who hands over the initial password out of band. `open`
 * keeps registration open to anybody who can reach the port, which also means anybody
 * who can reach the port can read every record; it is an explicit opt-in.
 *
 * The positional rule is unchanged where registration IS allowed: the first account
 * becomes the `Operator`, everybody after it is created by somebody holding `ROLES`
 * (`roles-and-permissions.md` D18.1 rule 3).
 *
 * AUTHORIZATION MOVED TO ACCESS ROLES (`shared/src/roles.ts`, migration 0013). What
 * survives here is the LEGACY `UserRole`, and it is now a DERIVED value: the role tables
 * are the source of truth, and `meta."user".role` is written as `'admin'` when the union
 * holds `ROLES` and `'member'` otherwise, purely so D18's rollback has something to fall
 * back to. NOTHING READS IT FOR AUTHORIZATION. It is dropped in a later migration once
 * the module is proven.
 */

export const USER_ROLES = ['admin', 'member'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const userRoleSchema = z.literal([...USER_ROLES]);

/**
 * LEGACY, and deliberately not deleted: ~30 sites in `packages/web/` use it for NAV
 * GATING, which `roles-and-permissions.md` D17 says follows separately. Every ENFORCEMENT
 * site on the server now asks `hasFlag(permissions, ...)` instead.
 */
export function isAdmin(role: UserRole): boolean {
  return role === 'admin';
}

/** What `GET /api/auth/me` returns. Never a hash, never a token. */
export interface AuthenticatedUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  /** Derived from `roles` (D18.1 rule 2). Display and nav only. */
  readonly role: UserRole;
  readonly roles: readonly RoleSummary[];
  /**
   * THE EFFECTIVE UNION, on the person rather than beside them.
   *
   * Section 6's "your permissions" panel renders a union ASSEMBLED from several roles plus
   * object overrides, and guardrail 7 wants a denied control DISABLED WITH A REASON rather
   * than hidden - both need this in the browser. It is the caller's own union and nobody
   * else's, so it discloses nothing they could not find by clicking.
   */
  readonly permissions: PermissionSet;
}

/**
 * How the current request authenticated. `apiKey` carries the key's LABEL, which is
 * what ends up in the actor context of every write it makes (INV-3), so an event says
 * "n8n-closes-handover" rather than an opaque id.
 */
export type AuthKind = 'session' | 'apiKey';

export interface AuthContext {
  readonly kind: AuthKind;
  /**
   * The access roles this principal holds, and their union (D2, D17).
   *
   * `role: UserRole` USED TO BE HERE and was removed rather than kept beside these two:
   * a legacy scalar next to the real answer is a scalar somebody eventually enforces on.
   * Its nine former readers are now flag checks, and the type error was the point.
   *
   * An unassigned API key resolves to `NO_PERMISSIONS` and can do nothing (D18 step 4).
   */
  readonly roles: readonly RoleSummary[];
  readonly permissions: PermissionSet;
  /** Present for sessions; absent for API keys, which are not people. */
  readonly user?: AuthenticatedUser | undefined;
  /** Present for API keys. */
  readonly apiKeyLabel?: string | undefined;
  readonly apiKeyId?: string | undefined;
  /**
   * INV-10: an AGENT-flagged key. Its writes carry actor source AGENT and route to
   * `meta.pending_change` unless an admin opted the object into direct writes, and it
   * is the only credential the MCP endpoint accepts.
   */
  readonly isAgent?: boolean | undefined;
}

export const SESSION_COOKIE_NAME = 'ofmos_session';

/** Bearer tokens carry this prefix so a leaked string is recognisable in a log. */
export const API_KEY_PREFIX = 'ofmk_';

/**
 * Sliding session window. Every authenticated request that finds the session more
 * than half expired extends it, so an active user is never logged out mid-work and an
 * abandoned session dies on its own.
 */
export const SESSION_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export const MIN_PASSWORD_LENGTH = 12;

/**
 * Length, not a character-class puzzle. Composition rules push people towards
 * `Passw0rd!` and a length floor does more for entropy than four required classes.
 */
export const passwordSchema = z.string().min(MIN_PASSWORD_LENGTH).max(256);

/** Emails are compared and stored lowercased: nobody means a different account by `Ben@`. */
export const emailSchema = z
  .string()
  .min(3)
  .max(320)
  .transform((value) => value.trim().toLowerCase())
  .refine((value) => z.email().safeParse(value).success, { message: 'not a valid email address' });

export const registerRequestSchema = z.strictObject({
  email: emailSchema,
  password: passwordSchema,
  name: z.string().min(1).max(120),
});

export const loginRequestSchema = z.strictObject({
  email: emailSchema,
  password: z.string().min(1).max(256),
});

export type RegisterRequest = z.infer<typeof registerRequestSchema>;
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/**
 * An account somebody holding `ROLES` creates (`POST /api/auth/users`), which is how
 * everybody except the bootstrap `Operator` gets in when `REGISTRATION_MODE=invite`.
 *
 * D18.1 rule 1: THE LEGACY SCALAR `role` IS REPLACED, not kept beside `roleIds`. A body
 * carrying both would be a merge conflict waiting to be resolved differently in two
 * places. An empty list means the `Coach` role, which the service resolves - a uuid
 * cannot be a schema default.
 */
export const createUserRequestSchema = z.strictObject({
  email: emailSchema,
  password: passwordSchema,
  name: z.string().min(1).max(120),
  roleIds: z.array(z.uuid()).default([]),
});

export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

/** What `GET /api/auth/users` returns per account. */
export interface UserAccount {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  /** Derived from `roles` (D18.1 rule 2). Never read for authorization. */
  readonly role: UserRole;
  readonly roles: readonly RoleSummary[];
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly lastLoginAt: string | null;
  /**
   * WHETHER THE CALLER MAY ACT ON THIS PRINCIPAL (wave 5, D-F3.3), computed per row against
   * the READER's own permissions.
   *
   * The target ceiling (D-F3.1) refuses an administrative action on somebody who holds more
   * than the actor, and a list that did not say so would offer every row the same controls
   * and let the server answer a third of them with a 403. THE ACTOR'S OWN ROW IS `false`,
   * because the self guards refuse it anyway and a row you may not touch is the accurate
   * thing to draw.
   */
  readonly manageable: boolean;
}

/**
 * `PATCH /api/auth/users/:id` (admin-area.md D14). Admin AND session-only (D3).
 *
 * NO EMAIL, deliberately: it is the login and the unique key here, so changing it
 * silently reassigns an account. The way to move a person to a new address is a new
 * account.
 *
 * Every field is optional and an empty body is a legal no-op rather than an error - the
 * screen sends the one thing the user changed.
 *
 * NO `role` EITHER, since D18.1 rule 4: role assignment is `PUT
 * /api/auth/users/:id/roles`, gated by `ROLES` and by guardrail 1, because the escalation
 * ceiling has to see the whole set being assigned rather than one scalar at a time.
 */
export const updateUserRequestSchema = z.strictObject({
  isActive: z.boolean().optional(),
  name: z.string().min(1).max(120).optional(),
});

export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

/**
 * `POST /api/auth/me/password` (admin-area.md D15). Any signed-in person.
 *
 * The current password is verified before the new one is written, which is what stops a
 * stolen cookie from locking the real owner out of their own account.
 */
export const changePasswordRequestSchema = z.strictObject({
  currentPassword: z.string().min(1).max(256),
  newPassword: passwordSchema,
});

export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

/**
 * PER-USER PREFERENCES (dashboard.md D33) - `GET/PUT /api/auth/me/preferences`.
 *
 * They live on the AUTH contract, beside the password routes, because the resource is
 * the caller's own user row and `POST /api/auth/me/password` is the precedent: a
 * per-caller mutation on the caller's own row, needing no admin. That is also what keeps
 * `AppClient` at nine surfaces - the browser gains two methods on `AuthClient` rather
 * than a tenth client.
 *
 * THE VALUE IS DELIBERATELY UNTYPED HERE. Each key owns its own shape and validates it
 * where it is read (`shared/dashboard.ts` for `dashboard.panels`); a union of every
 * preference shape in this file would make adding one a change to the auth contract.
 * What IS bounded is the KEY, because it is half of a primary key and an unbounded
 * string off the network has no business in one.
 */
export const USER_PREFERENCE_KEY_MAX = 120;

export const userPreferenceKeySchema = z
  .string()
  .min(1)
  .max(USER_PREFERENCE_KEY_MAX)
  .regex(/^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)*$/, 'a preference key is dotted lower-case segments');

export const setUserPreferenceSchema = z.strictObject({
  /** Any JSON document. `undefined` is not one, so a bodyless PUT is a 400 and not a null. */
  value: z.unknown().refine((value) => value !== undefined, 'a preference needs a value'),
});

export type SetUserPreferenceRequest = z.infer<typeof setUserPreferenceSchema>;

/** The whole bag, keyed by preference key. An unset key is ABSENT, never null. */
export type UserPreferences = Readonly<Record<string, unknown>>;

/**
 * What an admin password reset returns (admin-area.md D15).
 *
 * The generated password appears in this response and nowhere else, ever - the same
 * one-time hand-over the create flow already uses, because this instance has no mail
 * path and half of one would be worse than none.
 */
export interface ResetPasswordResult {
  readonly userId: string;
  readonly password: string;
  /** Sessions the reset threw away, so the admin knows they signed someone out. */
  readonly revokedSessions: number;
}

/** Characters in a generated password. 24 URL-safe characters is around 143 bits. */
export const GENERATED_PASSWORD_LENGTH = 24;

export const createApiKeyRequestSchema = z.strictObject({
  label: z.string().min(1).max(120),
  /**
   * D18 step 4: a key with no role assignment has ZERO permissions. An empty list is
   * therefore legal and means exactly that - a key that authenticates and can do
   * nothing - rather than being quietly widened to a default.
   *
   * D13: a role whose `canBeAssignedToApiKeys` is false is refused here, which is what
   * keeps `Operator` off every credential.
   */
  roleIds: z.array(z.uuid()).default([]),
  /**
   * Mints an AGENT key (INV-10): its writes are stamped `AGENT` and go to the approval
   * queue by default, and it is the only credential `POST /mcp` accepts. Off by
   * default - a plain integration key stays a plain integration key.
   */
  isAgent: z.boolean().default(false),
});

export interface ApiKeySummary {
  readonly id: string;
  readonly label: string;
  /** Derived from `roles`, exactly like a user's. Never read for authorization. */
  readonly role: UserRole;
  readonly roles: readonly RoleSummary[];
  readonly isAgent: boolean;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
  /** D-F3.3, as on a person: whether the reader's own permissions cover this key's. */
  readonly manageable: boolean;
}

/** Returned exactly once, by the create call. The server keeps only its hash. */
export interface CreatedApiKey extends ApiKeySummary {
  readonly key: string;
}

// --- invitations and password recovery (accounts F1) -------------------------

/**
 * ONE-TIME LINKS, AND WHY THE SERVER HANDS BACK A PATH.
 *
 * `admin-area.md` D15 decided against emailed links for an installation that had no mail
 * path; the owner's wave-4 brief reverses that half-way and the ledger records it: the
 * TOKEN mechanism ships now, the PROVIDER does not. So the normal transport is `manual` -
 * the server issues the token, the admin reads the link off the Members pane and hands it
 * over the way the initial password is handed over today.
 *
 * A PATH AND NOT A URL, because the only caller that can know the origin honestly is the
 * BROWSER the admin is already standing in (`window.location.origin`). Building an
 * absolute link from the request's `Host` header is host-header injection - an attacker
 * who can set that header decides where the person's token is sent - and building one from
 * configuration would make a correct link depend on `PUBLIC_BASE_URL` being right on an
 * instance nobody has configured for mail. A MAILED link has no browser to ask and is the
 * one place `PUBLIC_BASE_URL` is read.
 */
export const INVITATION_PATH_PREFIX = '/invite/';
export const PASSWORD_RESET_PATH_PREFIX = '/reset-password/';

/** Seven days. A week covers a holiday weekend and is short enough to be re-sent. */
export const INVITATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Sixty minutes, for a link the person asked for and is waiting on. */
export const PASSWORD_RESET_TTL_MS = 60 * 60 * 1000;
/**
 * Twenty-four hours for the link an ADMIN issues, because it travels by hand: a message
 * written after hours has to still work when the person reads it in the morning.
 */
export const ADMIN_RESET_LINK_TTL_MS = 24 * 60 * 60 * 1000;

/** Whether this instance can mail a link, which is what the recovery screen branches on. */
export type RecoveryMode = 'manual' | 'mail';

export interface RecoveryModeResponse {
  readonly mode: RecoveryMode;
}

/** What happened to the message: nothing (the admin carries it) or it was handed to mail. */
export type MailDelivery = 'manual' | 'sent';

export const createInvitationRequestSchema = z.strictObject({
  email: emailSchema,
  /** Empty means the `Coach` role, exactly as `createUserRequestSchema` does (D18.1 rule 1). */
  roleIds: z.array(z.uuid()).default([]),
});

export type CreateInvitationRequest = z.infer<typeof createInvitationRequestSchema>;

/**
 * What an invitation is doing right now. Derived from the row's three timestamps rather
 * than stored, because a status column and an `expires_at` are two facts that drift: an
 * invitation becomes expired by the clock moving, and nothing writes a row for that.
 */
export type InvitationStatus = 'open' | 'expired' | 'accepted' | 'revoked';

export interface InvitationSummary {
  readonly id: string;
  readonly email: string;
  readonly roles: readonly RoleSummary[];
  readonly status: InvitationStatus;
  readonly invitedBy: string | null;
  readonly createdAt: string;
  readonly expiresAt: string;
}

/**
 * The create response, and the plaintext token appears in it ONCE - the same one-time
 * hand-over the initial password already uses (`ResetPasswordResult`).
 */
export interface CreatedInvitation {
  readonly invitation: InvitationSummary;
  readonly acceptPath: string;
  readonly delivery: MailDelivery;
}

/** What the accept screen shows before anybody types: who invited whom, and until when. */
export interface InvitationPreview {
  readonly email: string;
  readonly invitedBy: string | null;
  readonly roleLabels: readonly string[];
  readonly expiresAt: string;
}

export const acceptInvitationRequestSchema = z.strictObject({
  name: z.string().min(1).max(120),
  password: passwordSchema,
});

export type AcceptInvitationRequest = z.infer<typeof acceptInvitationRequestSchema>;

export const passwordResetRequestSchema = z.strictObject({ email: emailSchema });

export interface PasswordResetRequestResult {
  readonly delivery: MailDelivery;
}

export const setPasswordRequestSchema = z.strictObject({ password: passwordSchema });

/** The admin-issued reset link (D-F1.4). The path is in this response and nowhere else. */
export interface ResetLinkResult {
  readonly userId: string;
  readonly resetPath: string;
  readonly expiresAt: string;
}

/** Why a one-time link was refused. The screen keys on this, never on the sentence. */
export type InvitationInvalidReason = 'expired' | 'used' | 'revoked' | 'unknown';
export type ResetInvalidReason = 'expired' | 'used' | 'unknown';
